import type { Entity, Webhook } from '@square/ignition';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { MarketPopover } from '@squareup/dex-ui-shared-market';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import styles from './reference-code-block.module.css';

/**
 * These functions are used to enable tooltips on attributes in code blocks.
 * This is used in the ReferenceCodeBlock component.
 * Original implementation:
 *  https://github.com/squareup/firestorm-docs/blob/master/app/components/code-example.js#L79-L305
 */

type TreeNode = {
  element: HTMLElement;
  children: TreeNode[];
  parent: TreeNode | null;
};
type ElementWithTreeNode = HTMLElement & { treeNode?: TreeNode };

/**
 * This function will take an HTML element, and attempt to derive
 * the property from this element. This is needed for attempting
 * to derive properties from code responses, where the hljs-attr
 * properties may be actual API Property objects.
 *
 * This function will take the current element name, and walk up the
 * persumed JSON tree until it hits the root. Using this, it can derive the actual property.
 *
 * It takes special consideration for Maps, since the key must be skipped. It also
 * handles invalid examples where necessary.
 * @param {EventTarget} target An HTML target that was clicked
 * @returns {Property} An Ignition Property, or null if none found
 */
function getPropertyFromTarget(
  target: EventTarget,
  entity: Entity | Webhook | undefined
) {
  if (
    !(target instanceof HTMLElement) ||
    !target.classList.contains('property') ||
    !entity
  ) {
    return null;
  }

  let currentEl = target as ElementWithTreeNode | undefined;

  const propertyPaths = [];

  /**
   * All attrs are siblings of each other, but we need to determine the parent hierarchy in
   * extremely large lists. To make this easier, we can simply chain calls to the parent tree node.
   */
  while (currentEl) {
    const propertyName = currentEl.textContent?.trim().replace(/"/g, '');
    if (propertyName) {
      propertyPaths.push(propertyName);
    }

    currentEl = currentEl.treeNode?.parent?.element;
  }

  const pathsInOrder = propertyPaths.reverse();
  let workingEntity = entity;
  let property;

  // We have to skip elements that are possibly keys from a Map. The easiest
  // way to do this is to simply skip over paths that don't give back a valid property
  // As well, any invalid properties will be skipped
  for (const path of pathsInOrder) {
    property = workingEntity.getProperty(path);
    // Break at the end, since it may be a primitive
    if (path === pathsInOrder[pathsInOrder.length - 1]) {
      break;
    }

    // We skip the property as it could be a map key
    if (!property) {
      continue;
    }

    // Try to fetch the next property. If none can be found, we assume this is not a valid
    // property to fetch
    if (property.isEntity && property.entity) {
      workingEntity = property.entity;
    } else if (property.containsEntity && property.itemEntity) {
      workingEntity = property.itemEntity;
    } else {
      return null;
    }
  }

  return property;
}

/**
 * The code response, after being processed by highlight.js, comes in as a flat DOM tree, where all attrs are siblings.
 * This makes determining what the attribute actually is in terms of the GraphEntity very difficult.
 * This is further made difficult when things like Map keys can be attributes.
 * To make property lookups from attributes an O(logn) complexity, we can preprocess the output, and build a tree using the
 * horizontal offset as an indicator of parent/child relationships, making parent lookups very quick later on.
 * @param {HTMLElement} element
 */
function buildAttrTree(element: HTMLElement) {
  const attributeEls = element.querySelectorAll(
    '.token.property'
  ) as NodeListOf<HTMLElement>;

  function buildNode(domElement: ElementWithTreeNode, parent: TreeNode | null) {
    // eslint-disable-next-line no-param-reassign
    domElement.treeNode = {
      element: domElement,
      children: [],
      parent,
    };

    if (parent) {
      parent.children.push(domElement.treeNode);
    }

    return domElement.treeNode;
  }

  if (attributeEls.length === 0) {
    return;
  }

  let currentNode;
  for (const attr of attributeEls) {
    if (!currentNode) {
      currentNode = buildNode(attr, null);
    } else if (currentNode.element.offsetLeft < attr.offsetLeft) {
      // The current node is the parent of the attr
      currentNode = buildNode(attr, currentNode);
    } else if (currentNode.element.offsetLeft > attr.offsetLeft) {
      // The current node is a child or an great granchild, etc, of the attr. Look for the right parent
      currentNode = currentNode.parent;
      while (currentNode && currentNode.element.offsetLeft > attr.offsetLeft) {
        currentNode = currentNode.parent;
      }

      if (!currentNode) {
        break;
      }

      // If the node we found is the same, then they are siblings
      if (currentNode.element.offsetLeft === attr.offsetLeft) {
        currentNode = buildNode(attr, currentNode.parent);
      } else {
        // Otherwise, this new node is a parent
        currentNode = buildNode(attr, currentNode);
      }
    } else {
      // Siblings. They have the same parent
      currentNode = buildNode(attr, currentNode.parent);
    }
  }
}

interface Params {
  code: string;
  showModal: boolean;
  isLoading: boolean | undefined;
  enabled: boolean;
  codeBlockEntity?: Entity | Webhook | undefined;
}

const useAttributeTooltips = ({
  code,
  showModal,
  isLoading,
  codeBlockEntity,
  enabled,
}: Params) => {
  const referenceElement = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<globalThis.HTMLMarketPopoverElement>(null);
  const [attrTooltip, setAttrTooltip] = useState<{
    title: string;
    desc: string;
  }>();

  const [isMarketComponentLoaded, marketPopoverRef] =
    useIsMarketComponentLoaded(popoverRef, 'market-popover');

  const { styles: popoverStyles, attributes } = usePopper(
    referenceElement.current,
    popoverRef.current,
    {
      placement: 'top',
    }
  );

  const hideAttributeTooltip = useCallback(() => {
    referenceElement.current = null;
    setAttrTooltip(undefined);
  }, []);

  useEffect(() => {
    const eventListenerFns: Function[] = [];

    function makeCodeAttrsInteractable(
      element: HTMLDivElement,
      entity: Entity | Webhook | undefined
    ) {
      const attributeEls = element.querySelectorAll(
        '.token.property'
      ) as NodeListOf<HTMLElement>;

      function addHoverableClass(e: MouseEvent) {
        if (e.target instanceof HTMLElement) {
          const property = getPropertyFromTarget(e.target, entity);
          if (property) {
            e.target.classList.add('property--hoverable');
          }
        }
      }

      for (const attr of attributeEls) {
        attr.addEventListener('mouseover', addHoverableClass, { once: true });
        attr.addEventListener('click', toggleTooltip);
        eventListenerFns.push(
          () => attr.removeEventListener('mouseover', addHoverableClass),
          () => attr.removeEventListener('click', toggleTooltip)
        );
      }
    }

    function toggleTooltip(event: Event) {
      const { target } = event;
      if (target instanceof HTMLElement) {
        const object = getPropertyFromTarget(target, codeBlockEntity);
        if (!object || !object.description) {
          referenceElement.current = null;
          setAttrTooltip(undefined);
        } else {
          referenceElement.current = target;
          setAttrTooltip({
            title: object.name,
            desc: object.description,
          });
        }
      }
    }

    if (enabled && !isLoading) {
      const codeBlock = document.querySelector('span.property')?.closest('div');
      if (codeBlock && showModal) {
        // We need to wait for the code block to be rendered in the modal before we can build the tree
        // Using isMarketComponentLoaded doesn't work if the user closes the modal and reopens it
        setTimeout(() => {
          buildAttrTree(codeBlock);
          makeCodeAttrsInteractable(codeBlock, codeBlockEntity);
        }, 100);
      } else if (codeBlock) {
        buildAttrTree(codeBlock);
        makeCodeAttrsInteractable(codeBlock, codeBlockEntity);
      }

      // Add a click listener to the window to close the tooltip if the user clicks outside of an attribute
      window.addEventListener('click', toggleTooltip);
      eventListenerFns.push(() => {
        referenceElement.current = null;
        window.removeEventListener('click', toggleTooltip);
      });
    }
    return () => {
      eventListenerFns.forEach((fn) => fn());
    };
  }, [
    code,
    enabled,
    isLoading,
    showModal,
    hideAttributeTooltip,
    codeBlockEntity,
  ]);

  const renderAttributeTooltip = useCallback(() => {
    if (!enabled) {
      return null;
    }
    return (
      <Box
        className={clsx({
          [styles['hidden'] as string]:
            !isMarketComponentLoaded || !referenceElement.current,
        })}
      >
        <MarketPopover
          ref={marketPopoverRef}
          className={clsx(styles.popover)}
          style={{ ...popoverStyles.popper }}
          {...attributes.popper}
        >
          <Paragraph20 weight="semi-bold" padding={{ bottom: '0.5x' }}>
            {attrTooltip?.title || ''}
          </Paragraph20>
          <Markdown
            markdown={attrTooltip?.desc || ''}
            textVariant="20"
            textColor="20"
          />
        </MarketPopover>
      </Box>
    );
  }, [
    enabled,
    attrTooltip,
    attributes.popper,
    isMarketComponentLoaded,
    marketPopoverRef,
    popoverStyles.popper,
  ]);

  return { renderAttributeTooltip };
};

export { useAttributeTooltips };
